import { useMutation } from '@tanstack/react-query'
import { useEffect, useContext } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/elements/button'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { updateUser } from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

import { SharedCardLayout } from './layout'

const PersonalInformationSection = () => {
  const { user, setUser } = useContext(UserStoreContext)

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      title: '',
    },
  })

  const { isPending: loadingUpdate, mutate: mutateUser } = useMutation({
    mutationFn: (data) => updateUser(data),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: (data) => {
      toast('Profile updated successfully.', 'success')
      setUser(data)
    },
  })

  useEffect(() => {
    if (user) {
      form.reset({
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email ?? '',
        title: user.title ?? '',
      })
    }
  }, [form, user])

  return (
    <SharedCardLayout title="Personal Information" description="Update your profile information.">
      <SharedCardLayout.Form
        formProps={form}
        onSubmit={form.handleSubmit((data) => mutateUser({ id: user.id, ...data }))}
      >
        <SharedCardLayout.Fields className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {[
            {
              name: 'firstName',
              label: 'First Name',
              placeholder: 'First Name',
              required: true,
            },
            {
              name: 'lastName',
              label: 'Last Name',
              placeholder: 'Last Name',
              required: true,
            },
            {
              name: 'email',
              label: 'Email',
              type: 'email',
              placeholder: 'Email',
              disabled: true, // temporarily disable email field, we'll add back with verfication later
              required: true,
            },
            {
              name: 'title',
              label: 'Title',
              placeholder: 'Job Title',
            },
          ].map(
            ({ name, label, type = 'text', required = false, disabled = false, placeholder }) => (
              <div key={name} className="sm:col-span-1">
                <FormField
                  control={form.control}
                  disabled={loadingUpdate || disabled}
                  rules={{ ...(required ? { required: 'This field is required' } : {}) }}
                  name={name}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor={name}>{label}</FormLabel>
                      <FormControl>
                        <Input
                          id={name}
                          data-testid={name}
                          placeholder={placeholder}
                          type={type}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ),
          )}
        </SharedCardLayout.Fields>

        <SharedCardLayout.Footer>
          <Button
            type="submit"
            disabled={loadingUpdate || !form.formState.isDirty}
            loading={loadingUpdate}
          >
            Save Changes
          </Button>
        </SharedCardLayout.Footer>
      </SharedCardLayout.Form>
    </SharedCardLayout>
  )
}

export default PersonalInformationSection
